import { NotificationProvider } from '@blocksuite/affine-shared/services';
import {
  insertPositionToIndex,
  type InsertToPosition,
} from '@blocksuite/affine-shared/utils';
import {
  computed,
  effect,
  type ReadonlySignal,
  signal,
} from '@preact/signals-core';
import type { DatabaseProjectionGroupSummary } from '@streamlain/database-identity';

import type { GroupBy, GroupProperty } from '../common/types.js';
import type { TypeInstance } from '../logical/type.js';
import { createTraitKey } from '../traits/key.js';
import { computedLock } from '../utils/lock.js';
import { DatabaseColumnNotLiveError } from '../view-manager/cell.js';
import type { Property } from '../view-manager/property.js';
import type { Row } from '../view-manager/row.js';
import type { SingleView } from '../view-manager/single-view.js';
import { defaultGroupBy } from './default.js';
import { findGroupByConfigByName, getGroupByService } from './matcher.js';
import {
  getGroupSortMode,
  type GroupSortMode,
  isGroupSortSupported,
  resolveGroupValueType,
  sortGroupKeys,
  withGroupSortMode,
} from './sort.js';
// Test
import type { GroupByConfig } from './types.js';

export type GroupInfo<
  RawValue = unknown,
  JsonValue = unknown,
  Data extends Record<string, unknown> = Record<string, unknown>,
> = {
  config: GroupByConfig;
  property: Property<RawValue, JsonValue, Data>;
  tType: TypeInstance;
};

export class Group<
  RawValue = unknown,
  JsonValue = unknown,
  Data extends Record<string, unknown> = Record<string, unknown>,
> {
  rows: Row[] = [];
  private globalCount?: number;
  totalKnown = true;
  complete = true;
  get totalCount(): number {
    return this.globalCount ?? this.rows.length;
  }
  set totalCount(count: number) {
    this.globalCount = count;
  }

  constructor(
    public readonly key: string,
    public readonly value: JsonValue,
    private readonly groupInfo: GroupInfo<RawValue, JsonValue, Data>,
    public readonly manager: GroupTrait
  ) {}

  get property() {
    return this.groupInfo.property;
  }
  name$ = computed(() => {
    const type = this.property.dataType$.value;
    return type ? this.groupInfo.config.groupName(type, this.value) : '';
  });
  private get config() {
    return this.groupInfo.config;
  }
  get tType() {
    return this.groupInfo.tType;
  }
  get view() {
    return this.config.view;
  }

  hide$ = computed(() => {
    const groupHide =
      this.manager.groupPropertiesMap$.value[this.key]?.hide ?? false;
    const emptyHidden =
      this.manager.hideEmpty$.value && this.totalKnown && this.totalCount === 0;
    return groupHide || emptyHidden;
  });

  hideSet(hide: boolean) {
    this.manager.setGroupHide(this.key, hide);
  }
}

export function toFrontendGroups(
  summaries: readonly DatabaseProjectionGroupSummary[],
  info: GroupInfo,
  manager: GroupTrait
): readonly Group[] {
  return summaries
    .filter(
      summary =>
        summary.columnId === info.property.id &&
        summary.name === info.config.name
    )
    .map(summary => {
      const group = new Group(summary.key, summary.value, info, manager);
      group.totalCount = summary.totalCount;
      group.totalKnown = summary.totalKnown;
      group.complete = summary.complete;
      group.rows = summary.rowIds.map(id => manager.view.rowGetOrCreate(id));
      return group;
    });
}

function hasGroupPropertiesAt(
  data: unknown,
  field: string
): data is Record<string, GroupProperty[] | undefined> {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  if (!(field in data)) {
    return false;
  }
  const value = (data as Record<string, unknown>)[field];
  return value === undefined || Array.isArray(value);
}

export class GroupTrait {
  // Default OFF — empty groups stay visible so users can drop cards
  // into them (Kanban) or see the full option list (Table/Gantt).
  // The stored group-by config can still override this via the
  // settings sync in `hideEmpty$` below.
  hideEmpty$ = signal<boolean>(false);

  supportsSort$ = computed(() => {
    const info = this.groupInfo$.value;
    return info ? isGroupSortSupported(info.tType) : false;
  });

  sortMode$ = computed(() =>
    this.supportsSort$.value ? getGroupSortMode(this.groupBy$.value) : 'default'
  );

  isExplicitSort$ = computed(() => this.sortMode$.value !== 'default');

  /**
   * Field name on the view data record that stores this trait's
   * per-group settings. Default is `groupProperties` (L1). Kanban's
   * second dimension passes `groupProperties2` so the two traits on
   * the same view never clobber each other's order / hide / manual
   * card-sort state.
   */
  private readonly groupPropertiesField: string;

  groupProperties$ = computed(() => {
    const data = this.view.data$.value;
    return hasGroupPropertiesAt(data, this.groupPropertiesField)
      ? (data[this.groupPropertiesField] ?? [])
      : [];
  });

  groupPropertiesMap$ = computed(() => {
    const map: Record<string, GroupProperty> = {};
    this.groupProperties$.value.forEach(g => {
      map[g.key] = g;
    });
    return map;
  });

  constructor(
    private readonly groupBy$: ReadonlySignal<GroupBy | undefined>,
    public view: SingleView,
    private readonly ops: {
      groupBySet: (g: GroupBy | undefined) => void;
      sortGroup: (keys: string[], asc?: boolean) => string[];
      sortRow: (groupKey: string, rows: Row[]) => Row[];
      changeGroupSort: (keys: string[]) => void;
      changeRowSort: (
        groupKeys: string[],
        groupKey: string,
        keys: string[]
      ) => void;
      changeGroupHide?: (key: string, hide: boolean) => void;
    },
    groupPropertiesField: string = 'groupProperties',
    /**
     * Whether the grouping behind this trait may be removed entirely
     * ("Remove Grouping" in the group-by settings). Kanban's L1
     * columns pass `false` — a kanban without column grouping is not
     * a kanban — while its L2 sub-grouping and Table/Gantt groupings
     * are removable.
     */
    public readonly canChangeGroupToNone: boolean = true
  ) {
    this.groupPropertiesField = groupPropertiesField;
    // Sync hideEmpty state with GroupBy data
    effect(() => {
      const hide = this.groupBy$.value?.hideEmpty;
      if (hide != null && this.hideEmpty$.value !== hide) {
        this.hideEmpty$.value = hide;
      }
    });
  }

  groupInfo$ = computed<GroupInfo | undefined>(() => {
    const groupBy = this.groupBy$.value;
    if (!groupBy) return;

    const property = this.view.propertyGetOrCreate(groupBy.columnId);
    if (!property) return;

    const tType = property.dataType$.value;
    if (!tType) return;

    const svc = getGroupByService(this.view.manager.dataSource);
    const res =
      groupBy.name != null
        ? (findGroupByConfigByName(
            this.view.manager.dataSource,
            groupBy.name
          ) ?? svc?.matcher.match(tType))
        : svc?.matcher.match(tType);

    if (!res) return;
    return { config: res, property, tType };
  });

  projection$ = computed(() => {
    const groupBy = this.groupBy$.value;
    return this.view.projectRows({
      groupBy,
      requestedPropertyIds: groupBy ? [groupBy.columnId] : [],
      scope: 'filtered-result',
    });
  });

  projectionComplete$ = computed(() => this.projection$.value.complete);

  projectionDiagnostics$ = computed(() => this.projection$.value.diagnostics);

  staticInfo$ = computed(() => {
    const info = this.groupInfo$.value;
    if (!info) return;
    const staticMap = Object.fromEntries(
      info.config
        .defaultKeys(info.tType)
        .map(({ key, value }) => [key, new Group(key, value, info, this)])
    );
    return { staticMap, groupInfo: info };
  });

  groupDataMap$ = computed(() => {
    const si = this.staticInfo$.value;
    if (!si) return;
    const { staticMap, groupInfo } = si;
    const summaries = this.projection$.value.groups;
    if (summaries !== undefined) {
      if (summaries.length)
        return Object.fromEntries(
          toFrontendGroups(summaries, groupInfo, this).map(group => [
            group.key,
            group,
          ])
        );
      return Object.fromEntries(
        Object.entries(staticMap).map(([key, group]) => {
          const pending = new Group(key, group.value, groupInfo, this);
          pending.totalKnown = false;
          pending.complete = false;
          return [key, pending];
        })
      );
    }

    // Create fresh Group instances with empty rows arrays
    const map: Record<string, Group> = {};
    Object.entries(staticMap).forEach(([key, group]) => {
      map[key] = new Group(key, group.value, groupInfo, this);
    });
    // Assign rows to their respective groups
    this.projection$.value.rows.forEach(row => {
      const cell = this.view.cellGetOrCreate(row.rowId, groupInfo.property.id);
      const jv = cell.jsonValue$.value;
      const keys = groupInfo.config.valuesGroup(jv, groupInfo.tType);
      keys.forEach(({ key, value }) => {
        if (!map[key]) map[key] = new Group(key, value, groupInfo, this);
        map[key].rows.push(row);
      });
    });
    return map;
  });

  private sortGroupKeys(map: Record<string, Group>, info: GroupInfo) {
    if (this.projection$.value.groups !== undefined) return Object.keys(map);
    const isDate = info.config.matchType.name === 'Date';
    return sortGroupKeys({
      keys: Object.keys(map),
      valueForKey: key => map[key]?.value,
      valueType: resolveGroupValueType(info.tType),
      mode: this.sortMode$.value,
      manualKeys: this.groupProperties$.value.map(group => group.key),
      dateMode: isDate ? info.config.name : undefined,
    });
  }

  groupsDataList$ = computedLock(
    computed(() => {
      const map = this.groupDataMap$.value;
      if (!map) return;

      const gi = this.groupInfo$.value;
      if (!gi) return;
      const ordered = this.sortGroupKeys(map, gi);

      const allGroups = ordered
        .map(k => map[k])
        .filter((g): g is Group => !!g && !this.isGroupHidden(g.key));

      // When hideEmpty is on, hide empty groups — but only if at least one
      // group has rows. Otherwise show all so the user can add cards.
      if (this.hideEmpty$.value) {
        const hasAnyRows = allGroups.some(g => g.totalCount > 0);
        if (hasAnyRows) {
          return allGroups.filter(g => !g.totalKnown || g.totalCount > 0);
        }
      }

      return allGroups;
    }),
    this.view.isLocked$
  );

  /**
   * Computed list of groups including hidden ones, used by settings UI.
   */
  groupsDataListAll$ = computedLock(
    computed(() => {
      const map = this.groupDataMap$.value;
      const info = this.groupInfo$.value;
      if (!map || !info) return;

      const orderedKeys = this.sortGroupKeys(map, info);

      const visible: Group[] = [];
      const hidden: Group[] = [];
      orderedKeys
        .map(key => map[key])
        .filter((g): g is Group => g != null)
        .forEach(g => {
          if (g.hide$.value) {
            hidden.push(g);
          } else {
            visible.push(g);
          }
        });
      return [...visible, ...hidden];
    }),
    this.view.isLocked$
  );

  /** Whether all groups are currently hidden */
  allHidden$ = computed(() => {
    const map = this.groupDataMap$.value;
    if (!map) return false;
    return Object.keys(map).every(key => this.isGroupHidden(key));
  });

  /**
   * Toggle hiding of empty groups.
   */

  setHideEmpty(value: boolean) {
    this.hideEmpty$.value = value;
    const gb = this.groupBy$.value;
    if (gb) {
      this.ops.groupBySet({ ...gb, hideEmpty: value });
    }
  }

  isGroupHidden(key: string): boolean {
    return this.groupPropertiesMap$.value[key]?.hide ?? false;
  }

  setGroupHide(key: string, hide: boolean) {
    this.ops.changeGroupHide?.(key, hide);
  }

  setGroupSortMode(mode: GroupSortMode) {
    const gb = this.groupBy$.value;
    if (gb) {
      this.ops.groupBySet(withGroupSortMode(gb, mode));
    }
  }

  /** Compatibility wrapper for existing date-setting consumers. */
  setDateSortOrder(asc: boolean) {
    this.setGroupSortMode(asc ? 'asc' : 'desc');
  }

  addToGroup(rowId: string, key: string) {
    const groupMap = this.groupDataMap$.value;
    const groupInfo = this.groupInfo$.value;
    if (!groupMap || !groupInfo) {
      return;
    }
    const addTo = groupInfo.config.addToGroup;
    if (addTo === false) {
      return;
    }
    const v = groupMap[key]?.value;
    if (v != null) {
      const newValue = addTo(
        v,
        this.view.cellGetOrCreate(rowId, groupInfo.property.id).jsonValue$.value
      );
      this.view
        .cellGetOrCreate(rowId, groupInfo.property.id)
        .valueSet(newValue);
    }
    const map = this.groupDataMap$.value;
    const info = this.groupInfo$.value;
    if (!map || !info) return;

    const addFn = info.config.addToGroup;
    if (addFn === false) return;

    const group = map[key];
    if (!group) return;

    const current = group.value;
    // Handle both null and non-null values to ensure proper group assignment
    const newVal = addFn(
      current,
      this.view.cellGetOrCreate(rowId, info.property.id).jsonValue$.value
    );
    this.view.cellGetOrCreate(rowId, info.property.id).valueSet(newVal);
  }
  changeGroupMode(modeName: string) {
    const propId = this.property$.value?.id;
    if (!propId) return;
    const next: GroupBy = {
      type: 'groupBy',
      columnId: propId,
      name: modeName,
      hideEmpty: this.hideEmpty$.value,
    };
    const currentSort = this.groupBy$.value?.sort;
    this.ops.groupBySet(
      currentSort ? { ...next, sort: { ...currentSort } } : next
    );
  }

  changeGroup(columnId: string | undefined) {
    if (columnId == null) {
      this.ops.groupBySet(undefined);
      return;
    }
    const column = this.view.propertyGetOrCreate(columnId);
    const meta = this.view.manager.dataSource.propertyMetaGet(
      column.type$.value
    );
    if (meta) {
      const gb = defaultGroupBy(
        this.view.manager.dataSource,
        meta,
        column.id,
        column.data$.value
      );
      if (gb) {
        gb.hideEmpty = this.hideEmpty$.value;
      }
      this.ops.groupBySet(gb);
    }
  }

  property$ = computed(() => this.groupInfo$.value?.property);

  get addGroup() {
    return this.property$.value?.meta$.value?.config.addGroup;
  }

  updateData = (data: NonNullable<unknown>) => {
    const prop = this.property$.value;
    if (!prop) return;
    this.view.propertyGetOrCreate(prop.id).dataUpdate(() => data);
  };

  changeGroupSort(keys: string[]) {
    if (this.isExplicitSort$.value) return;
    this.ops.changeGroupSort(keys);
  }

  moveCardTo(
    rowId: string,
    fromGroupKey: string | undefined,
    toGroupKey: string,
    position: InsertToPosition
  ) {
    const groupMap = this.groupDataMap$.value;
    if (!groupMap) {
      return;
    }
    if (fromGroupKey !== toGroupKey) {
      const propertyId = this.property$.value?.id;
      if (!propertyId) {
        return;
      }
      const remove =
        this.groupInfo$.value?.config.removeFromGroup ?? (() => null);
      const group = fromGroupKey != null ? groupMap[fromGroupKey] : undefined;
      let newValue: unknown = null;
      if (group) {
        newValue = remove(
          group.value,
          this.view.cellGetOrCreate(rowId, propertyId).jsonValue$.value
        );
      }
      const addTo = this.groupInfo$.value?.config.addToGroup;
      if (addTo === false || addTo == null) {
        return;
      }
      newValue = addTo(groupMap[toGroupKey]?.value ?? null, newValue);
      // The card stays in its group when the group column is not live yet.
      if (!this.writeGroupValue(rowId, propertyId, newValue)) return;
    }
    const visibleRows =
      groupMap[toGroupKey]?.rows
        .filter(row => row.rowId !== rowId)
        .map(row => row.rowId) ?? [];
    // Group summaries hydrate only their window. The stored manual ID order
    // remains the full destination sequence and must survive a bounded drop.
    const rows =
      this.projection$.value.groups !== undefined
        ? [
            ...new Set([
              ...(this.groupPropertiesMap$.value[toGroupKey]
                ?.manuallyCardSort ?? []),
              ...visibleRows,
            ]),
          ].filter(id => id !== rowId)
        : visibleRows;
    const index = insertPositionToIndex(position, rows, row => row);
    rows.splice(index, 0, rowId);
    const groupKeys = Object.keys(groupMap);
    this.ops.changeRowSort(groupKeys, toGroupKey, rows);
  }

  moveGroupTo(groupKey: string, position: InsertToPosition) {
    const groups = this.groupsDataList$.value;
    if (!groups) {
      return;
    }
    const keys = groups.map(v => v!.key);
    keys.splice(
      keys.findIndex(key => key === groupKey),
      1
    );
    const index = insertPositionToIndex(position, keys, key => key);
    keys.splice(index, 0, groupKey);
    this.changeGroupSort(keys);
  }

  removeFromGroup(rowId: string, key: string) {
    const groupMap = this.groupDataMap$.value;
    if (!groupMap) {
      return;
    }
    const propertyId = this.property$.value?.id;
    if (!propertyId) {
      return;
    }
    const remove =
      this.groupInfo$.value?.config.removeFromGroup ?? (() => undefined);
    const newValue = remove(
      groupMap[key]?.value ?? null,
      this.view.cellGetOrCreate(rowId, propertyId).jsonValue$.value
    );
    this.view.cellGetOrCreate(rowId, propertyId).valueSet(newValue);
  }

  updateValue(rows: string[], value: unknown) {
    const propertyId = this.property$.value?.id;
    if (!propertyId) {
      return;
    }
    // Every row gets the value; one notice covers the rows that cannot.
    const refused = rows.filter(
      rowId => !this.writeGroupValue(rowId, propertyId, value, false)
    );
    if (refused.length)
      this.reportColumnNotLive(
        new DatabaseColumnNotLiveError(propertyId, refused[0]!)
      );
  }

  /** False when the group column is not live yet; the user is told once. */
  private writeGroupValue(
    rowId: string,
    propertyId: string,
    value: unknown,
    report = true
  ): boolean {
    try {
      this.view.cellGetOrCreate(rowId, propertyId).jsonValueSet(value);
      return true;
    } catch (error) {
      if (!(error instanceof DatabaseColumnNotLiveError)) throw error;
      if (report) this.reportColumnNotLive(error);
      return false;
    }
  }

  /** Shows the refusal as a toast; hosts without notifications fail loud. */
  private reportColumnNotLive(error: DatabaseColumnNotLiveError): void {
    const notification =
      this.view.manager.dataSource.serviceGet(NotificationProvider);
    if (!notification) throw error;
    notification.toast(error.message);
  }
}

export const groupTraitKey = createTraitKey<GroupTrait>('group');
export const group2TraitKey = createTraitKey<GroupTrait>('group2');

export const sortByManually = <T>(
  arr: T[],
  getId: (v: T) => string,
  ids: string[]
) => {
  const map = new Map(arr.map(v => [getId(v), v]));
  const result: T[] = [];
  for (const id of ids) {
    const value = map.get(id);
    if (value) {
      map.delete(id);
      result.push(value);
    }
  }
  result.push(...map.values());
  return result;
};
